import { useState } from "react";
import {
  Search,
  FileText,
  Filter,
  ExternalLink,
  Edit2,
  Calendar,
  Layers,
  User,
  ArrowRight,
  RotateCcw
} from "lucide-react";
import { Link } from "react-router-dom";
import { LoadingState, ErrorState } from "../../components/LoadingState";
import { EmptyState } from "../../components/common/EmptyState";
import { apiGet } from "../../services/api";
import { formatDate } from "../../utils/date";
import type { Article, Paged } from "../../types";

export function ArchiveSearchPage() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [results, setResults] = useState<Article[]>([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;

    try {
      setLoading(true);
      setError(null);
      setSearched(true);
      const params = new URLSearchParams();
      params.set("q", query.trim());
      if (status !== "all") params.set("status", status);
      params.set("pageSize", "50");

      const res = await apiGet<Paged<Article>>(`/api/articles?${params.toString()}`);
      setResults(res.items || []);
    } catch (err: any) {
      setError(err.message || "Failed to search publication archive");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-page-flow">
      {/* Header */}
      <header className="admin-section-header">
        <div>
          <div className="admin-breadcrumb-tag">
            <Search size={13} className="text-[#C99A2E]" />
            <span>ARCHIVE INDEX</span>
          </div>
          <h1 className="admin-title">Newsroom Search & Index</h1>
          <p className="admin-subtitle">
            Perform comprehensive full-text searches across all historic stories, drafts, and manuscripts.
          </p>
        </div>
      </header>

      {/* Search Input Card */}
      <div className="admin-card-panel">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by keywords, headline terms, author names, or topics..."
              className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-300 rounded-lg text-sm focus:outline-none focus:bg-white focus:border-[#C99A2E]"
            />
          </div>

          <div className="w-full sm:w-48">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold focus:outline-none focus:border-[#C99A2E]"
            >
              <option value="all">All Editorial Statuses</option>
              <option value="published">Published Only</option>
              <option value="draft">Drafts Only</option>
              <option value="review">Under Review</option>
              <option value="scheduled">Scheduled</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="admin-btn-primary whitespace-nowrap"
          >
            <Search size={16} />
            <span>{loading ? "Searching..." : "Search Archive"}</span>
          </button>
        </form>
      </div>

      {/* Results */}
      {loading ? (
        <LoadingState label="Indexing manuscripts across editorial archive..." />
      ) : error ? (
        <ErrorState message={error} />
      ) : searched && results.length === 0 ? (
        <div className="admin-card-panel p-0 overflow-hidden">
          <EmptyState
            icon={Search}
            badge="ARCHIVE SEARCH"
            title="No archive results found"
            description={`No manuscripts matched your search query "${query}". Try searching with different keywords, tags, or adjusting the status filter.`}
            actionText="Reset Search Filter"
            onAction={() => {
              setQuery("");
              setStatus("all");
              setResults([]);
              setSearched(false);
            }}
            actionIcon={RotateCcw}
          />
        </div>
      ) : results.length > 0 ? (
        <div className="admin-card-panel p-0 overflow-hidden">
          <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center justify-between">
            <span>Found {results.length} matching {results.length === 1 ? "story" : "stories"}</span>
            <span className="text-[11px] font-normal text-slate-500 font-mono">Query: "{query}"</span>
          </div>

          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Manuscript Title</th>
                  <th>Category</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map((article) => (
                  <tr key={article.id}>
                    <td>
                      <div className="admin-table-title-cell">
                        <Link
                          to={`/admin/articles/${article.id}/edit`}
                          className="admin-table-story-title"
                        >
                          {article.title}
                        </Link>
                        <span className="admin-table-story-slug">/{article.slug}</span>
                      </div>
                    </td>
                    <td>
                      <span className="admin-category-tag">{article.category.name}</span>
                    </td>
                    <td>
                      <span className="admin-author-text">{article.author.name}</span>
                    </td>
                    <td>
                      <span className={`status-pill status-${article.status}`}>
                        <span className="status-dot" />
                        <span>{article.status}</span>
                      </span>
                    </td>
                    <td>
                      <span className="text-xs text-slate-500 font-medium">
                        {formatDate(article.published_at || article.created_at)}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div className="admin-row-actions">
                        <Link
                          to={`/admin/articles/${article.id}/edit`}
                          className="action-icon-btn"
                          title="Edit Manuscript"
                        >
                          <Edit2 size={14} />
                        </Link>
                        {article.status === "published" && (
                          <a
                            href={`/article/${article.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="action-icon-btn"
                            title="View Public Story"
                          >
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
